import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

// ============================================
// CONSTANTS
// ============================================

const QUESTIONS = [
  "1. Feeling nervous, anxious, or on edge",
  "2. Not being able to stop or control worrying",
  "3. Worrying too much about different things",
  "4. Trouble relaxing",
  "5. Being so restless that it's hard to sit still",
  "6. Becoming easily annoyed or irritable",
  "7. Feeling afraid as if something awful might happen",
];

const VALUE_LABELS = {
  0: "Not at all",
  1: "Several days",
  2: "More than half the days",
  3: "Nearly every day",
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v === 0) return 0;
    if (v !== null && v !== undefined) {
      const s = String(v);
      if (s.trim() !== "") return v;
    }
  }
  return "";
};

const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") return ["true", "yes", "y", "1"].includes(v.trim().toLowerCase());
  return !!v;
};

const parseJsonDeep = (input, maxDepth = 5) => {
  let v = input;
  let depth = 0;
  try {
    while (typeof v === "string" && depth < maxDepth) {
      v = JSON.parse(v);
      depth++;
    }
  } catch {
    // Stop on parse errors
  }
  return v;
};

const getSeverity = (score) => {
  const s = Number(score) || 0;
  if (s >= 15) return "Severe anxiety";
  if (s >= 10) return "Moderate anxiety";
  if (s >= 5) return "Mild anxiety";
  return "Minimal anxiety";
};

const getDifficultyFromFlags = (data) => {
  if (!data) return "—";
  if (toBool(data.ExtremelyDifficult) || toBool(data.extremelyDifficult)) return "Extremely difficult";
  if (toBool(data.VertDifficult) || toBool(data.vertDifficult) || toBool(data.VeryDifficult) || toBool(data.veryDifficult)) return "Very difficult";
  if (toBool(data.SomeWhatDifiicult) || toBool(data.someWhatDifiicult) || toBool(data.SomewhatDifficult) || toBool(data.somewhatDifficult)) return "Somewhat difficult";
  if (toBool(data.DifficultAll) || toBool(data.difficultAll) || toBool(data.NotDifficultAtAll) || toBool(data.notDifficultAtAll)) return "Not difficult at all";
  return "—";
};

// ============================================
// TABLE HELPER COMPONENTS
// ============================================

const Row = ({ label, value }) => (
  <tr>
    <td style={{ width: "50%", padding: "8px", border: "1px solid #ddd", fontWeight: 600 }}>
      {label}
    </td>
    <td style={{ width: "50%", padding: "8px", border: "1px solid #ddd", whiteSpace: "pre-wrap" }}>
      {value ?? "—"}
    </td>
  </tr>
);

const Section = ({ title }) => (
  <tr>
    <td colSpan={2} style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold" }}>
      {title}
    </td>
  </tr>
);

// ============================================
// MAIN COMPONENT
// ============================================

const GAD7Preview = ({ data }) => {
  const [form, setForm] = useState({});
  const [gad7, setGad7] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { patientFormId, formTitle } = data || {};

  // ============================================
  // FETCH DATA
  // ============================================
  useEffect(() => {
    if (!patientFormId) {
      setLoading(false);
      setError("No Patient Form ID provided");
      return;
    }

    let active = true;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("neuro_patientportal_auth_token");

        if (!token) {
          throw new Error("Authentication token not found. Please log in.");
        }

        const headers = { Authorization: `Bearer ${token}` };

        const formUrl = `${process.env.REACT_APP_API_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`;
        const gad7Url = `${process.env.REACT_APP_API_URL}/Patients/GetPatientGAD7ByFormId?patientFormId=${patientFormId}`;

        const [formResponse, gad7Response] = await Promise.all([
          fetch(formUrl, { headers }),
          fetch(gad7Url, { headers }),
        ]);

        if (!active) return;

        if (!formResponse.ok) {
          throw new Error(`Form load failed (${formResponse.status})`);
        }

        const formRaw = await formResponse.json();
        const formData = Array.isArray(formRaw) ? (formRaw[0] || {}) : (formRaw || {});

        let gad7Data = {};
        if (gad7Response.ok) {
          const gad7Text = await gad7Response.text();
          if (gad7Text && gad7Text.trim() !== "" && gad7Text !== "null") {
            gad7Data = parseJsonDeep(gad7Text) || {};
          }
        }

        if (active) {
          setForm(formData);
          setGad7(gad7Data);
        }

      } catch (err) {
        if (active) {
          console.error("Error fetching data:", err);
          setError(err.message || "Failed to load GAD-7 Assessment.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [patientFormId]);

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: "60vh", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // ============================================
  // ERROR STATE
  // ============================================
  if (error) {
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  }

  // ============================================
  // PREPARE DATA FOR DISPLAY
  // ============================================

  const headerTitle = firstNonEmpty(formTitle) || "Generalized Anxiety Disorder (GAD-7)";
  const dosVal = fmtDate(firstNonEmpty(gad7.DateOfService, gad7.dateOfService, form.assessmentDate));
  const fileNo = firstNonEmpty(form.accountNo, gad7.accountNo) || "—";
  const clientName = firstNonEmpty(form.patientName, gad7.ClientName, gad7.clientName, gad7.patientName) || "—";
  const dobVal = fmtDate(firstNonEmpty(gad7.DateOfBirth, gad7.dateOfBirth, form.dob));

  const answers = [
    parseInt(firstNonEmpty(gad7.Q1, gad7.q1), 10),
    parseInt(firstNonEmpty(gad7.Q2, gad7.q2), 10),
    parseInt(firstNonEmpty(gad7.Q3, gad7.q3), 10),
    parseInt(firstNonEmpty(gad7.Q4, gad7.q4), 10),
    parseInt(firstNonEmpty(gad7.Q5, gad7.q5), 10),
    parseInt(firstNonEmpty(gad7.Q6, gad7.q6), 10),
    parseInt(firstNonEmpty(gad7.Q7, gad7.q7), 10),
  ].map((v) => (Number.isNaN(v) ? -1 : v));

  const totalScore = firstNonEmpty(gad7.totalScore, gad7.TotalScore) ||
    answers.reduce((sum, a) => sum + (a >= 0 ? a : 0), 0);

  const severity = getSeverity(totalScore);
  const difficulty = getDifficultyFromFlags(gad7);
  const breakdown = answers.map((a) => (a >= 0 ? a : 0)).join(" + ") + ` = ${totalScore}`;

  // ============================================
  // RENDER
  // ============================================
  return (
    <Paper sx={{ p: 0, borderRadius: 2 }}>
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px",
          zIndex: (t) => t.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 16 }}>
          <strong>DOS:</strong> {dosVal}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {headerTitle}
        </Typography>

        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      {/* Patient strip */}
      <Box sx={{ px: 4 }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="body2"><strong>File #:</strong> {fileNo}</Typography>
          </Grid>
          <Grid item xs={4} textAlign="center">
            <Typography variant="body2"><strong>Client Name:</strong> {clientName}</Typography>
          </Grid>
          <Grid item xs={4} textAlign="right">
            <Typography variant="body2"><strong>DOB:</strong> {dobVal}</Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 3 }} />
      </Box>

      {/* Body */}
      <Box sx={{ px: 4, pb: 4 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Over the <strong>last 2 weeks</strong>, how often have you been bothered by{" "}
          <strong>any</strong> of the following problems?
        </Typography>

        {/* Responses Table */}
        <TableContainer component={Paper} elevation={0} sx={{ mb: 3, border: "1px solid #ddd" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: "60%" }}>Question</TableCell>
                <TableCell sx={{ fontWeight: 700, width: "20%", textAlign: "center" }}>Score</TableCell>
                <TableCell sx={{ fontWeight: 700, width: "20%" }}>Response</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {QUESTIONS.map((question, index) => {
                const answer = answers[index];
                const responseLabel = answer >= 0 ? (VALUE_LABELS[answer] || `Score ${answer}`) : "—";
                return (
                  <TableRow key={index}>
                    <TableCell>{question}</TableCell>
                    <TableCell sx={{ textAlign: "center", fontWeight: 600 }}>
                      {answer >= 0 ? answer : "—"}
                    </TableCell>
                    <TableCell>{responseLabel}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Score Summary */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12pt", marginBottom: "24px" }}>
          <tbody>
            <Section title="Score & Interpretation" />
            <Row label="For Office Coding" value={breakdown} />
            <Row label="Total Score" value={String(totalScore)} />
            <Row label="Severity" value={severity} />
          </tbody>
        </table>

        {/* Functional Difficulty */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12pt" }}>
          <tbody>
            <Section title="Functional Difficulty" />
            <Row
              label="If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?"
              value={difficulty}
            />
          </tbody>
        </table>

        {/* Severity Guide */}
        <Box sx={{ mt: 4, p: 2, border: "1px solid #ddd", borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            GAD-7 Severity Guide:
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2">• 0-4: Minimal anxiety</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2">• 5-9: Mild anxiety</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2">• 10-14: Moderate anxiety</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2">• 15-21: Severe anxiety</Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
};

export default GAD7Preview;